import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Grid,
    Typography,
    Alert,
    CircularProgress
} from '@mui/material';
import { APIPostcodeSearch } from '../Api';

/**
 * CustomerCaptureForm - Captures customer details when no FLG ID exists or FLG is unavailable
 * Creates a new site/contact that can be used to proceed with a quote
 */
const CustomerCaptureForm = ({ open, onSubmit, onCancel }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [postcodeResults, setPostcodeResults] = useState([]);

    // Customer details
    const [customer, setCustomer] = useState({
        title: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        mobile: ''
    });

    // Address details
    const [address, setAddress] = useState({
        address1: '',
        address2: '',
        address3: '',
        town: '',
        county: '',
        postcode: '',
        country: 'United Kingdom'
    });

    const handleCustomerChange = (field) => (event) => {
        setCustomer(prev => ({ ...prev, [field]: event.target.value }));
        setError('');
    };

    const handleAddressChange = (field) => (event) => {
        setAddress(prev => ({ ...prev, [field]: event.target.value }));
        setError('');
    };

    const handlePostcodeLookup = async () => {
        if (!address.postcode || address.postcode.length < 5) {
            setError('Please enter a valid postcode');
            return;
        }

        setLoading(true);
        try {
            const results = await APIPostcodeSearch(address.postcode);
            if (results && results.length > 0) {
                setPostcodeResults(results);
                // Auto-fill first result
                const first = results[0];
                setAddress(prev => ({
                    ...prev,
                    address1: first.address1 || first.line_1 || '',
                    address2: first.address2 || first.line_2 || '',
                    address3: first.address3 || first.line_3 || '',
                    town: first.town || first.post_town || '',
                    county: first.county || '',
                    postcode: first.postcode || address.postcode
                }));
            } else {
                setError('No addresses found for this postcode');
            }
        } catch (err) {
            setError('Failed to lookup postcode. Please enter address manually.');
        }
        setLoading(false);
    };

    const validateForm = () => {
        if (!customer.firstName.trim()) {
            setError('First name is required');
            return false;
        }
        if (!customer.lastName.trim()) {
            setError('Last name is required');
            return false;
        }
        if (!customer.email.trim()) {
            setError('Email is required');
            return false;
        }
        if (!address.postcode.trim()) {
            setError('Postcode is required');
            return false;
        }
        if (!address.address1.trim()) {
            setError('Address line 1 is required');
            return false;
        }
        return true;
    };

    const handleSubmit = () => {
        if (!validateForm()) return;

        // Build the customer info object
        const customerInfo = {
            name: `${customer.firstName} ${customer.lastName}`.trim(),
            first_name: customer.firstName,
            email: customer.email,
            full_address: [
                address.address1,
                address.address2,
                address.address3,
                address.town,
                address.county,
                address.postcode
            ].filter(Boolean).join(', '),
            postcode: address.postcode,
            assigned_rep: '',
            flg_rec: {}
        };

        // Build the site object
        const siteData = {
            site_id: 0,
            site_name: `${customer.lastName} - ${address.postcode}`,
            address: { ...address },
            contacts: [{
                id: 0,
                title: customer.title,
                first: customer.firstName,
                last: customer.lastName,
                email: customer.email,
                homephone: customer.phone,
                mobilephone: customer.mobile,
                position: 'Customer',
                notes: ''
            }]
        };

        onSubmit({ customerInfo, siteData });
    };

    return (
        <Dialog
            open={open}
            maxWidth="md"
            fullWidth
            disableEscapeKeyDown
        >
            <DialogTitle>
                <Typography variant="h6">
                    Customer Details Required
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    No customer record found. Please enter the customer details to proceed with the quote.
                </Typography>
            </DialogTitle>

            <DialogContent dividers>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {/* Customer Information */}
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                    Customer Information
                </Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={2}>
                        <TextField
                            fullWidth
                            label="Title"
                            value={customer.title}
                            onChange={handleCustomerChange('title')}
                            size="small"
                            placeholder="Mr/Mrs/Ms"
                        />
                    </Grid>
                    <Grid item xs={12} sm={5}>
                        <TextField
                            fullWidth
                            required
                            label="First Name"
                            value={customer.firstName}
                            onChange={handleCustomerChange('firstName')}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={5}>
                        <TextField
                            fullWidth
                            required
                            label="Last Name"
                            value={customer.lastName}
                            onChange={handleCustomerChange('lastName')}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            required
                            label="Email"
                            type="email"
                            value={customer.email}
                            onChange={handleCustomerChange('email')}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            fullWidth
                            label="Phone"
                            value={customer.phone}
                            onChange={handleCustomerChange('phone')}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            fullWidth
                            label="Mobile"
                            value={customer.mobile}
                            onChange={handleCustomerChange('mobile')}
                            size="small"
                        />
                    </Grid>
                </Grid>

                {/* Address Information */}
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                    Installation Address
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            required
                            label="Postcode"
                            value={address.postcode}
                            onChange={handleAddressChange('postcode')}
                            size="small"
                            onKeyPress={(e) => e.key === 'Enter' && handlePostcodeLookup()}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Button
                            variant="outlined"
                            onClick={handlePostcodeLookup}
                            disabled={loading}
                            sx={{ height: '40px' }}
                        >
                            {loading ? <CircularProgress size={20} /> : 'Lookup Address'}
                        </Button>
                    </Grid>
                    <Grid item xs={12} sm={4} />

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            required
                            label="Address Line 1"
                            value={address.address1}
                            onChange={handleAddressChange('address1')}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Address Line 2"
                            value={address.address2}
                            onChange={handleAddressChange('address2')}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Address Line 3"
                            value={address.address3}
                            onChange={handleAddressChange('address3')}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Town/City"
                            value={address.town}
                            onChange={handleAddressChange('town')}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="County"
                            value={address.county}
                            onChange={handleAddressChange('county')}
                            size="small"
                        />
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                {onCancel && (
                    <Button onClick={onCancel} color="inherit">
                        Cancel
                    </Button>
                )}
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    color="primary"
                    disabled={loading}
                >
                    Continue to Quote
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CustomerCaptureForm;
